import { Link } from "react-router-dom";
import styled from "styled-components";

import { OutlineButton } from "components/Button";
import { Status } from "types/Offer";
import ThemeInterface from "types/ThemeInterface";

interface CrossSell {
  $iscrossselling: boolean;
}

type CardImages = {
  desktop: string;
  tablet: string;
  mobile: string;
};

interface ICardContent extends CrossSell {
  $backgroundImages?: CardImages;
  $status?: Status;
  $darkenBackgroundImage?: boolean;
  $hasStatusBar?: boolean;
}

interface IStatusBar {
  color: "blue" | "black" | "yellow" | "red" | "white";
  $status?: Status;
}

interface ICardPartnerLogo extends CrossSell {
  active: boolean;
  $status?: Status;
}
interface IStyledButtonContainer extends CrossSell {
  $isMoreInfoButton: boolean;
  $isInBasketButton?: boolean;
}

interface ICardInfo extends CrossSell, IStyledButtonContainer {}

const getBackgroundColor = ({ theme, color }: { theme: ThemeInterface; color: string }): string | null => {
  switch (color) {
    case "blue":
      return theme.customTheme.palette.getBackgroundColor.bgColorBlue;
    case "black":
      return theme.customTheme.palette.getBackgroundColor.bgColorBlack;
    case "yellow":
      return theme.customTheme.palette.getBackgroundColor.bgColorYellow;
    case "red":
      return theme.customTheme.palette.getBackgroundColor.bgColorRed;
    case "white":
      return theme.customTheme.palette.getBackgroundColor.bgColorWhite;
    default:
      return null;
  }
};

const getColor = ({
  theme,
  color,
  $status,
}: {
  theme: ThemeInterface;
  color: string;
  $status: Status | undefined;
}): string | null => {
  switch (color) {
    case "yellow":
      return theme.customTheme.palette.getColor.blackStandard;
    case "black":
      return $status === Status.provisioning
        ? theme.customTheme.palette.getColor.whiteStandard
        : theme.customTheme.palette.getColor.greenStatus;
    case "blue":
      return theme.customTheme.palette.getColor.whiteStandard;
    case "red":
      return theme.customTheme.palette.getColor.whiteStandard;
    case "white":
      return theme.customTheme.palette.getColor.redStandard;
    default:
      return null;
  }
};

export const StyledLink = styled(Link)<CrossSell>`
  text-decoration: none;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;

  display: flex;
  flex-direction: column;
  position: relative;

  height: ${({ $iscrossselling }) => ($iscrossselling ? "162px" : "276px")};

  &:visited {
    text-decoration: none;
    color: black;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    height: ${({ $iscrossselling }) => ($iscrossselling ? "158px" : "276px")};
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    height: ${({ $iscrossselling }) => ($iscrossselling ? "162px" : "204px")};
  }
`;

export const StatusBar = styled.div<IStatusBar>`
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  background-color: ${({ theme, color }) => getBackgroundColor({ theme, color })};
  color: ${({ theme, color, $status }) => getColor({ theme, color, $status })};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
`;

export const StatusBarText = styled.span`
  font-size: ${({ theme }) => theme.customTheme.pxToRem(12)};
  line-height: ${({ theme }) => theme.customTheme.pxToRem(16)};
  font-weight: 700;
  font-family: ThemeFontBold;
`;

export const CardContent = styled.div<ICardContent>`
  border-radius: 12px;

  background: ${({ $darkenBackgroundImage }) =>
      $darkenBackgroundImage ? "linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3))," : ""}
    url(${({ $backgroundImages }) => $backgroundImages?.mobile || ""});
  background-repeat: no-repeat;
  background-position: center;
  background-position-y: ${({ $hasStatusBar }) => ($hasStatusBar ? "32px" : "0px")};

  ${({ $iscrossselling, $hasStatusBar }) =>
    $iscrossselling
      ? {
          "background-size": $hasStatusBar ? "cover" : "327px 162px",
        }
      : {
          "background-size": "cover",
        }}

  height: 100%;
  width: 100%;

  display: flex;
  flex-direction: ${({ $iscrossselling }) => ($iscrossselling ? "row" : "column")};
  align-items: ${({ $iscrossselling }) => ($iscrossselling ? "flex-start" : "center")};
  padding: ${({ $iscrossselling }) => ($iscrossselling ? "20px" : "32px 16px 16px")};
  gap: 12px;
  justify-content: ${({ $iscrossselling, $status }) =>
    $iscrossselling || ($status !== Status.purchasable && $status !== Status.purchasableExternally)
      ? "space-between"
      : "flex-end"};

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    background: ${(props) =>
        props.$darkenBackgroundImage ? "linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3))," : ""}
      url(${(props) => props.$backgroundImages?.tablet || ""});
    background-position-y: ${({ $hasStatusBar }) => ($hasStatusBar ? "32px" : "0px")};
    ${({ $iscrossselling, $hasStatusBar }) =>
      $iscrossselling
        ? {
            "background-size": $hasStatusBar ? "cover" : "517px 158px",
          }
        : {
            "background-size": $hasStatusBar ? "cover" : "250px 276px",
          }}
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    background: ${(props) =>
        props.$darkenBackgroundImage ? "linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3))," : ""}
      url(${(props) => props.$backgroundImages?.desktop || ""});
    background-position-y: ${({ $hasStatusBar }) => ($hasStatusBar ? "32px" : "0px")};
    ${({ $iscrossselling, $hasStatusBar }) =>
      $iscrossselling
        ? {
            "background-size": $hasStatusBar ? "330px 130px" : "330px 161px",
          }
        : {
            "background-size": $hasStatusBar ? "cover" : "291px 204px",
          }}
  }

  ${({ $iscrossselling }) =>
    $iscrossselling
      ? {
          "justify-content": "flex-end",
          "align-items": "end",
        }
      : {
          "justify-content": "end",
        }}
`;

export const CardPartnerLogo = styled.div<ICardPartnerLogo>`
  flex: ${(props) => (props.active ? 1 : 0)};
  display: flex;
  flex-direction: ${({ $iscrossselling }) => ($iscrossselling ? "column" : "row")};
  justify-content: center;
  align-items: ${({ $iscrossselling }) => ($iscrossselling ? "flex-start" : "center")};
  gap: ${({ $iscrossselling }) => ($iscrossselling ? "10px" : "0")};
  margin-top: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${({ $iscrossselling }) =>
    $iscrossselling &&
    `
        left: 20px;
        top: 52px;
        transform: none;
  `}
`;

export const LogoItem = styled.div<CrossSell>`
  display: ${({ $iscrossselling }) => ($iscrossselling ? "flex" : "block")};

  & img {
    height: ${({ $iscrossselling }) => ($iscrossselling ? "44px" : "80px")};
    width: ${({ $iscrossselling }) => ($iscrossselling ? "88px" : "80px")};
    align-self: ${({ $iscrossselling }) => ($iscrossselling ? "center" : "unset")};
    object-fit: contain;
  }
`;

export const CardInfo = styled.div<ICardInfo>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $iscrossselling }) => ($iscrossselling ? "flex-end" : "flex-start")};
  gap: ${({ $iscrossselling }) => ($iscrossselling ? "12px" : "4px")};
  width: 100%;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    width: ${({ $iscrossselling }) => ($iscrossselling ? "30%" : "100%")};
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    display: flex;
    flex-direction: ${({ $iscrossselling }) => ($iscrossselling ? "column" : "row")};
    justify-content: ${({ $isMoreInfoButton, $iscrossselling }) =>
      $isMoreInfoButton || $iscrossselling ? "flex-end" : "space-between"};
    align-items: ${({ $iscrossselling }) => ($iscrossselling ? "flex-end" : "center")};
  }
`;

export const PricesContainer = styled.div<CrossSell>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: ${({ $iscrossselling }) => ($iscrossselling ? "flex-end" : "flex-start")};
  text-align: ${({ $iscrossselling }) => ($iscrossselling ? "right" : "left")};
  width: 100%;
`;

export const PriceText = styled.span`
  color: ${({ theme }) => theme.customTheme.palette.white.standard};
  font-size: ${({ theme }) => theme.customTheme.pxToRem(16)};
  line-height: ${({ theme }) => theme.customTheme.pxToRem(20)};
  font-family: ThemeFontBold;
  font-weight: 700;
  width: 100%;
  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const Partner = styled.span<CrossSell>`
  color: ${({ theme }) => theme.customTheme.palette.Partner.color};
  font-family: ThemeFontRegular;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;

  display: inline;
  order: ${({ $iscrossselling }) => ($iscrossselling ? 1 : 0)};
  font-size: ${({ theme, $iscrossselling }) =>
    $iscrossselling ? theme.customTheme.pxToRem(14) : theme.customTheme.pxToRem(16)};
  line-height: ${({ theme, $iscrossselling }) =>
    $iscrossselling ? theme.customTheme.pxToRem(18) : theme.customTheme.pxToRem(20)};
`;

export const StyledButtonContainer = styled.div<IStyledButtonContainer>`
  padding-top: ${({ $iscrossselling }) => ($iscrossselling ? "0" : "12px")};
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: auto;

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    width: ${({ $isMoreInfoButton, $isInBasketButton, $iscrossselling }) =>
      ($isMoreInfoButton || $isInBasketButton) && !$iscrossselling
        ? "auto"
        : !$isMoreInfoButton && !$isInBasketButton && !$iscrossselling
        ? "auto"
        : "100%"};
    max-width: ${({ $isMoreInfoButton, $isInBasketButton, $iscrossselling }) =>
      ($isMoreInfoButton || $isInBasketButton) && !$iscrossselling
        ? "50%"
        : !$isMoreInfoButton && !$isInBasketButton && !$iscrossselling
        ? "50%"
        : "100%"};
  }
`;

export const AddToCartButton = styled(OutlineButton)`
  width: 100%;
  font-family: ThemeFontBold;
`;

export const MoreInfoButton = styled(OutlineButton)`
  width: 100%;
  font-family: ThemeFontBold;
`;

export const InBasketButton = styled(OutlineButton)`
  width: 100%;
  font-family: ThemeFontBold;
`;
